import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  Users, 
  Zap, 
  Shield, 
  Crown, 
  ArrowRight
} from 'lucide-react';
import NFTCard from '../components/NFTCard';
import StatsCard from '../components/StatsCard';
import SupremeLogo from '../assets/svgs/SupremeLogo';
import NFTHero from '../assets/svgs/NFTHero';
import BlockchainNetwork from '../assets/svgs/BlockchainNetwork';
import MarketplaceFlow from '../assets/svgs/MarketplaceFlow';
import ArbitrumBadge from '../assets/svgs/ArbitrumBadge';
import StatsVisualization from '../assets/svgs/StatsVisualization';

const Home = () => {
  const featuredNFTs = [
    {
      id: 1,
      name: "Supreme Genesis #1",
      image: "https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=400&h=400&fit=crop",
      price: "0.1",
      currency: "ETH",
      creator: "0x1234...5678",
      likes: 42,
      views: 128
    },
    {
      id: 2,
      name: "Digital Crown #5",
      image: "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&h=400&fit=crop",
      price: "0.25",
      currency: "ETH",
      creator: "0x8765...4321",
      likes: 38,
      views: 95
    },
    {
      id: 3,
      name: "Royal Artifact #12",
      image: "https://images.unsplash.com/photo-1642790105077-9c5a4a5b5b5b?w=400&h=400&fit=crop",
      price: "0.5",
      currency: "SUP",
      creator: "0xabcd...efgh",
      likes: 67,
      views: 203
    },
    {
      id: 4,
      name: "Supreme Collection #8",
      image: "https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=400&h=400&fit=crop",
      price: "0.15",
      currency: "ETH",
      creator: "0x5678...9abc",
      likes: 29,
      views: 87
    }
  ];

  const stats = [
    {
      title: "Total Volume",
      value: "2,847.5",
      currency: "ETH",
      change: "+12.5%",
      icon: TrendingUp,
      color: "from-green-400 to-emerald-500"
    },
    {
      title: "Active Users",
      value: "1,234",
      change: "+8.2%",
      icon: Users,
      color: "from-blue-400 to-cyan-500"
    },
    {
      title: "NFTs Listed",
      value: "5,678",
      change: "+15.3%",
      icon: Zap,
      color: "from-purple-400 to-pink-500"
    },
    {
      title: "Collections",
      value: "89",
      change: "+3.1%",
      icon: Shield,
      color: "from-orange-400 to-red-500"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative py-20 text-center"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20 blur-3xl"></div>
        <div className="relative z-10">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="flex justify-center mb-8"
          >
            <SupremeLogo className="w-20 h-20" animated={true} />
          </motion.div>
          
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-5xl md:text-7xl font-bold mb-6"
          >
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              Supreme NFT
            </span>
            <br />
            <span className="text-white">Marketplace</span>
          </motion.h1>
          
          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto"
          >
            Discover, collect, and trade extraordinary NFTs on the Supreme Chain. 
            Experience the future of digital ownership with our advanced marketplace.
          </motion.p>
          
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/marketplace"
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <span>Explore Marketplace</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to="/create"
              className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200 border border-white/20"
            >
              Create NFT
            </Link>
          </motion.div>
        </div>
      </motion.section>

      {/* Stats Section */}
      <motion.section 
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1, duration: 0.8 }}
        className="py-16"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.2 + index * 0.1, duration: 0.8 }}
            >
              <StatsCard {...stat} />
            </motion.div>
          ))}
        </div>
        
        {/* Stats Visualization */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="bg-white/5 rounded-2xl p-6 border border-white/10"
        >
          <h3 className="text-xl font-semibold text-white mb-4 text-center">Trading Volume Growth</h3>
          <StatsVisualization className="w-full h-48" />
        </motion.div>
      </motion.section>

      {/* Featured NFTs Section */}
      <motion.section 
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="py-16"
      >
        <div className="flex items-center justify-between mb-12">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Featured NFTs</h2>
            <p className="text-gray-400">Discover the most popular and trending NFTs</p>
          </div>
          <Link
            to="/marketplace"
            className="flex items-center space-x-2 text-purple-400 hover:text-purple-300 transition-colors"
          >
            <span>View All</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featuredNFTs.map((nft, index) => (
            <motion.div
              key={nft.id}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.6 + index * 0.1, duration: 0.8 }}
            >
              <NFTCard nft={nft} />
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* How It Works Section */}
      <motion.section 
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="py-16"
      >
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">How It Works</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            From creation to collection, see how our marketplace connects creators and collectors
          </p>
        </div>
        
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 2, duration: 0.8 }}
          className="bg-white/5 rounded-2xl p-6 border border-white/10 mb-12"
        >
          <MarketplaceFlow className="w-full h-64" />
        </motion.div>
      </motion.section>

      {/* Blockchain Network Section */}
      <motion.section 
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 2.2, duration: 0.8 }}
        className="py-16"
      >
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">Powered by Arbitrum</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Built on the most advanced Layer 2 blockchain for lightning-fast transactions
          </p>
        </div>
        
        <div className="flex justify-center mb-8">
          <ArbitrumBadge className="w-24 h-24" animated={true} />
        </div>
        
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 2.4, duration: 0.8 }}
          className="bg-white/5 rounded-2xl p-6 border border-white/10"
        >
          <BlockchainNetwork className="w-full h-48" />
        </motion.div>
      </motion.section>

      {/* Features Section */}
      <motion.section 
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 2.6, duration: 0.8 }}
        className="py-16"
      >
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">Why Choose Supreme NFT?</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Built on Arbitrum with advanced features and seamless user experience
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 2.8, duration: 0.8 }}
            className="text-center p-6 bg-white/5 rounded-2xl border border-white/10"
          >
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-4 rounded-xl w-fit mx-auto mb-4">
              <Zap className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Lightning Fast</h3>
            <p className="text-gray-400">
              Built on Arbitrum for ultra-low gas fees and instant transactions
            </p>
          </motion.div>
          
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 3, duration: 0.8 }}
            className="text-center p-6 bg-white/5 rounded-2xl border border-white/10"
          >
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4 rounded-xl w-fit mx-auto mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Secure & Trusted</h3>
            <p className="text-gray-400">
              Advanced security features and anti-whale protection for safe trading
            </p>
          </motion.div>
          
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 3.2, duration: 0.8 }}
            className="text-center p-6 bg-white/5 rounded-2xl border border-white/10"
          >
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-4 rounded-xl w-fit mx-auto mb-4">
              <Crown className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Premium Experience</h3>
            <p className="text-gray-400">
              Beautiful UI, advanced features, and seamless user experience
            </p>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default Home;
